import { Accordion, Button, Loader, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type FC } from 'react';
import { getAmenitiesByIdQueryOptions } from '../api/get-amenities.query';

type AmenitiesExpandableListProps = {
  facilityId: string;
};

export const AmenitiesExpandableList: FC<AmenitiesExpandableListProps> = ({
  facilityId,
}) => {
  const [openedAccordionIds, setOpenedAccordionIds] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    ...getAmenitiesByIdQueryOptions({ facilityId: +facilityId }),
    select: (amenityGroup) =>
      amenityGroup
        .filter(({ amenities }) =>
          amenities.some(({ isSelected }) => isSelected)
        )
        .map((group) => ({
          ...group,
          amenities: group.amenities.filter(({ isSelected }) => isSelected),
        })),
  });

  const isAmenitiesNotExist = data?.length === 0;
  const isAllExpanded = openedAccordionIds.length === data?.length;

  const handleExpandAll = () => {
    const allIds = data?.map(({ category }) => category.id.toString()) ?? [];
    setOpenedAccordionIds(allIds);
  };

  return (
    <div className="bg-neutral-1 border-neutral-4 flex w-full flex-col gap-6 border p-3">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          <Typography variant="h4" weight="medium">
            {`Amenities ${isAmenitiesNotExist ? ' – Not Found' : ''}`}
          </Typography>
          {isLoading && <Loader size={20} />}
        </div>
        {!isAmenitiesNotExist && !isLoading && (
          <Button
            variant="secondary"
            disabled={isLoading || !!isAmenitiesNotExist}
            onClick={() =>
              isAllExpanded ? setOpenedAccordionIds([]) : handleExpandAll()
            }
          >
            {isAllExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        )}
      </div>

      {!isAmenitiesNotExist && !isLoading && (
        <div className="flex flex-col">
          <Accordion.Root
            type="multiple"
            value={openedAccordionIds}
            onValueChange={setOpenedAccordionIds}
          >
            {data?.map(({ category, amenities }) => (
              <Accordion.Item
                className="border-neutral-5 border-b"
                key={category.id}
                value={category.id.toString()}
              >
                <Accordion.Trigger className="py-4">
                  <Typography variant="h5" weight="medium">
                    {category.name}
                  </Typography>
                </Accordion.Trigger>
                <Accordion.Content className="pt-2 pb-6">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {amenities.map(({ id, name }) => (
                      <div className="flex items-center space-x-2" key={id}>
                        <Typography noWrap title={name}>
                          {name}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </div>
      )}
    </div>
  );
};
